import {
  ApplicationElement,
  html,
  nothing,
} from '../application_element/element.js';
import {
  formatBucket,
  formatValue,
  isTimeseries,
} from '../../lib/chart_format.js';
import { BRAND_COLORS, colorsFor } from '../../lib/chart_colors.js';
import translations from './translations.js';
import style from './style.lit.css';

/**
 * Base for the analytics charts. Not registered as an element — it holds the
 * reading of a series that every chart does identically, so a category is
 * labelled, formatted and coloured the same whichever chart it lands in.
 *
 * Rows are `{ label, value }` for a series and `{ at, value }` for a timeseries;
 * the shape is detected from the rows rather than declared.
 *
 * @prop rows - The series ( array, default: [] )
 * @prop caption - Names what the chart shows ( string )
 * @prop grain - 'day' | 'week' | 'month', how time buckets are labelled ( string, default: 'day' )
 * @prop format - 'number' | 'percent' ( string, default: 'number' )
 * @prop precision - Fraction digits ( number | null )
 */
export default class ChartElement extends ApplicationElement {
  static properties = {
    rows: { type: Array },
    caption: { type: String },
    grain: { type: String },
    format: { type: String },
    precision: { type: Number },
  };

  static translations = translations;

  static styles = [ApplicationElement.styles, style];

  constructor() {
    super();
    this.rows = [];
    this.grain = 'day';
    this.format = 'number';
    this.precision = null;
  }

  get empty() {
    return !Array.isArray(this.rows) || this.rows.length === 0;
  }

  get timeseries() {
    return isTimeseries(this.rows);
  }

  get totalValue() {
    return this.rows.reduce((sum, row) => sum + this.numberOf(row), 0);
  }

  /** Bars are read against the largest bucket, so the biggest row fills the track. */
  get peak() {
    return Math.max(...this.rows.map(row => this.numberOf(row)), 0);
  }

  /**
   * Assigned for the series as a whole, not per row, so two categories can
   * never come out the same colour. Recomputed only when the rows change.
   */
  get colors() {
    if (this.rows !== this.coloredRows) {
      this.coloredRows = this.rows;
      // Time buckets have no identity to carry, so they take the lead brand
      // colour. A category keeps its colour across every chart that shows it.
      this.palette = this.timeseries
        ? this.rows.map(() => BRAND_COLORS[0])
        : colorsFor(this.rows.map(row => row.label));
    }

    return this.palette;
  }

  /** One `label: value` per row, for the text alternative. */
  get descriptions() {
    return this.rows.map(row => this.descriptionOf(row));
  }

  labelOf(row) {
    if (this.timeseries)
      return formatBucket(row.at, {
        locale: this.i18n.locale,
        grain: this.grain,
      });

    // A null label is a real bucket — records with no value for the grouped
    // field — not a missing row.
    return row.label ?? this.t('none');
  }

  /** Terser than `labelOf` — an axis tick has one bucket's width to fit in. */
  tickLabelOf(row) {
    if (!this.timeseries) return this.labelOf(row);

    return formatBucket(row.at, {
      locale: this.i18n.locale,
      grain: this.grain,
      short: true,
    });
  }

  numberOf(row) {
    return Number(row?.value) || 0;
  }

  /** Any figure, read the way this chart reads its values. */
  formatted(value) {
    return formatValue(value, {
      locale: this.i18n.locale,
      format: this.format,
      precision: this.precision,
    });
  }

  valueOf(row) {
    return this.formatted(row.value);
  }

  /** What a bucket says on hover, and what it reads as without the chart. */
  descriptionOf(row) {
    return `${this.labelOf(row)}: ${this.valueOf(row)}`;
  }

  colorOf(row, index) {
    return this.colors[index];
  }

  renderCaption() {
    return this.caption
      ? html`<figcaption class="caption">${this.caption}</figcaption>`
      : nothing;
  }

  /** The caption names what is missing, so it outlives the rows. */
  renderEmpty() {
    return html`
      <figure>
        ${this.renderCaption()}
        <p class="empty">${this.t('empty')}</p>
      </figure>
    `;
  }
}

// Registers the shared keys as a fallback scope, so every chart resolves
// `none` and `empty` without restating them. Not a tag name — nothing defines
// this element, exactly as with `ef-application-element`.
ChartElement.extendTranslationsAs('xs-chart-element');
